import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FiMail, FiPhone, FiMapPin, FiSend, FiCheck } from 'react-icons/fi';
import emailjs from '@emailjs/browser';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.section`
  padding: 8rem 2rem;
//   background: linear-gradient(135deg, #f6f9fc 0%, #f1f4f8 100%);
  position: relative;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 4rem;
  position: relative;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactInfo = styled.div`
  animation: ${fadeIn} 0.6s ease-out;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #2d3436 0%, #636e72 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: #636e72;
  font-size: 1.1rem;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const ContactDetails = styled.div`
  margin-top: 3rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  
  svg {
    font-size: 1.5rem;
    color: #0984e3;
    margin-right: 1rem;
  }
  
  p {
    color: #636e72;
  }
`;

const FormContainer = styled(motion.div)`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
`;

const Form = styled.form`
  display: grid;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.9);
  
  &:focus {
    outline: none;
    border-color: #0984e3;
    box-shadow: 0 0 0 4px rgba(9, 132, 227, 0.1);
  }

  &::placeholder {
    color: #b2bec3;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.9);
  
  &:focus {
    outline: none;
    border-color: #0984e3;
    box-shadow: 0 0 0 4px rgba(9, 132, 227, 0.1);
  }

  &::placeholder {
    color: #b2bec3;
  }
`;

const SubmitButton = styled.button`
  background: #0984e3;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background: #0873c4;
    transform: translateY(-2px);
  }

  &:disabled {
    background: #b2bec3;
    cursor: not-allowed;
  }

  svg {
    font-size: 1.2rem;
  }
`;

const SuccessMessage = styled(motion.div)`
  color: #00b894;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
        to_email: 'info@morpheus-tech.io',
        reply_to: formData.email
      };

      emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);

      const result = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams
      );

      if (result.status === 200) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Failed to send email:', error);
      alert('Failed to send message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }

    setTimeout(() => {
      setIsSubmitted(false);
    }, 5000);
  };

  return (
    <Container id="contact">
      <ContentWrapper>
        <ContactInfo>
          <Title>Get in Touch</Title>
          <Subtitle>
            No more struggles in finding the right partner. At Morpheus Tech, we don’t just provide short-term functional fixes—we deliver advanced, future-ready solutions designed to stand the test of time.          </Subtitle>
          <ContactDetails>
            <ContactItem>
              <FiMail />
              <p>info@morpheus-tech.io</p>
            </ContactItem>
            <ContactItem>
              <FiPhone />
              <p>+61 435868704</p>
            </ContactItem>
            <ContactItem>
              <FiMapPin />
              <p>15 Moore St, Acton, ACT 2601</p>
            </ContactItem>
          </ContactDetails>
        </ContactInfo>

        <FormContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Form onSubmit={handleSubmit}>
            <InputGroup>
              <Input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </InputGroup>
            <InputGroup>
              <Input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </InputGroup>
            <InputGroup>
              <Input
                type="text"
                name="subject"
                placeholder="Subject"
                value={formData.subject}
                onChange={handleChange}
                required
              />
            </InputGroup>
            <InputGroup>
              <TextArea
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleChange}
                required
              />
            </InputGroup>
            <SubmitButton 
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
              {!isSubmitting && <FiSend />}
            </SubmitButton>
            {isSubmitted && (
              <SuccessMessage
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <FiCheck /> Message sent successfully!
              </SuccessMessage>
            )}
          </Form>
        </FormContainer>
      </ContentWrapper>
    </Container>
  );
};

export default ContactSection; 